import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { useSelector } from 'react-redux';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import useThemeColor from '../../hooks/useThemeColor';
import useDarkMode from '../../hooks/useDarkMode';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import CustomModal from '../../components/CustomComponents/modal/customModal';
import moment from 'moment';
import swal from 'sweetalert';
import '../order/ManageOrderFilters.css';

const pickValueIgnoreCase = (obj, candidates) => {
  if (!obj) return undefined;
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }

  const lowerCaseMap = Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});

  for (const key of candidates) {
    const lowerKey = key.toLowerCase();
    if (lowerCaseMap.hasOwnProperty(lowerKey)) {
      return lowerCaseMap[lowerKey];
    }
  }

  return undefined;
};

const getNumberValue = (record = {}, keys = [], defaultValue = 0) => {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return num;
      }
    }
  }
  return defaultValue;
};

const normalizeUnpaidHours = (record = {}) => {
  return {
    userName: record.UserName || record.userName || '',
    firstName: record.FirstName || record.firstName || '',
    lastName: record.LastName || record.lastName || '',
    email: record.Email || record.email || '',
    baseRate: getNumberValue(record, ['baseRate', 'BaseRate', 'RatePerHour', 'ratePerHour']),
    totalRegularHours: getNumberValue(record, ['totalRegularHours', 'TotalRegularHours']),
    totalOvertimeHours: getNumberValue(record, ['totalOvertimeHours', 'TotalOvertimeHours']),
    totalBreakHours: getNumberValue(record, ['totalBreakHours', 'TotalBreakHours']),
    unpaidBreakHours: getNumberValue(record, ['unpaidBreakHours', 'UnpaidBreakHours']),
    paidBreakHours: getNumberValue(record, ['paidBreakHours', 'PaidBreakHours']),
    extraBreakHours: getNumberValue(record, ['extraBreakHours', 'ExtraBreakHours']),
    adjustedPaidBreakHours: getNumberValue(record, ['adjustedPaidBreakHours', 'AdjustedPaidBreakHours']),
    totalWorkingHours: getNumberValue(record, ['totalWorkingHours', 'TotalWorkingHours']),
    regularEarnings: getNumberValue(record, ['regularEarnings', 'RegularEarnings']),
    overtimeEarnings: getNumberValue(record, ['overtimeEarnings', 'OvertimeEarnings']),
    paidBreakEarnings: getNumberValue(record, ['paidBreakEarnings', 'PaidBreakEarnings']),
    totalEarnings: getNumberValue(record, ['totalEarnings', 'TotalEarnings']),
    entryCount: getNumberValue(record, ['entryCount', 'EntryCount']),
    firstShiftDate: record.FirstShiftDate || record.firstShiftDate || null,
    lastShiftDate: record.LastShiftDate || record.lastShiftDate || null,
  };
};

const computeAdjustedUnpaidHours = (normalized, staffProfile) => {
  if (!normalized) return null;

  const isSalaryStaff = Boolean(staffProfile?.isSalaryStaff);
  const totalRegularHours = normalized.totalRegularHours || 0;
  const totalOvertimeHours = normalized.totalOvertimeHours || 0;
  const unpaidBreakHours = normalized.unpaidBreakHours || 0;
  const paidBreakHours = normalized.paidBreakHours || 0;
  const extraBreakHours = normalized.extraBreakHours || 0;

  // Use adjusted paid break hours from API (already has extra breaks subtracted)
  const adjustedPaidBreakHours = normalized.adjustedPaidBreakHours ?? (paidBreakHours > 0 && extraBreakHours > 0 ? Math.max(0, paidBreakHours - extraBreakHours) : paidBreakHours);

  const baseRate = staffProfile?.ratePerHour ?? normalized.baseRate ?? 0;
  const overtimeRateFallback = staffProfile?.overTimeHourlyRate || baseRate;

  const regularRate =
    totalRegularHours > 0
      ? (normalized.regularEarnings || 0) / totalRegularHours
      : baseRate;

  const overtimeRate =
    totalOvertimeHours > 0
      ? (normalized.overtimeEarnings || 0) / totalOvertimeHours
      : overtimeRateFallback || regularRate;

  let adjustedRegularHours = totalRegularHours;
  let adjustedOvertimeHours = totalOvertimeHours;
  let remainingDeduction = isSalaryStaff ? 0 : unpaidBreakHours;

  // For hourly staff: deduct extra break hours from payable hours
  if (!isSalaryStaff && extraBreakHours > 0) {
    remainingDeduction += extraBreakHours;
  }

  if (remainingDeduction > 0 && adjustedRegularHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedRegularHours);
    adjustedRegularHours -= deduction;
    remainingDeduction -= deduction;
  }

  if (remainingDeduction > 0 && adjustedOvertimeHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedOvertimeHours);
    adjustedOvertimeHours -= deduction;
    remainingDeduction -= deduction;
  }

  // For earnings: add adjusted paid break hours to regular hours
  // Use paid break earnings from API (already calculated with adjusted hours) or calculate it
  const adjustedPaidBreakEarnings = normalized.paidBreakEarnings || (adjustedPaidBreakHours * regularRate);

  const payableRegularHoursWithBreaks = adjustedRegularHours + adjustedPaidBreakHours;
  const payableRegularEarnings = payableRegularHoursWithBreaks * regularRate;
  const payableOvertimeEarnings = adjustedOvertimeHours * overtimeRate;
  const payableWorkingEarnings = payableRegularEarnings + payableOvertimeEarnings;

  // Total earnings includes regular (with paid breaks) + overtime
  const payableTotalEarnings = payableWorkingEarnings;

  return {
    ...normalized,
    regularRate,
    overtimeRate,
    payableRegularHours: adjustedRegularHours,
    payableOvertimeHours: adjustedOvertimeHours,
    payableHours: adjustedRegularHours + adjustedOvertimeHours,
    payableRegularEarnings,
    payableOvertimeEarnings,
    payableWorkingEarnings,
    payableTotalEarnings,
    unpaidBreakAdjustmentHours: isSalaryStaff ? 0 : unpaidBreakHours,
    adjustedPaidBreakHours,
    adjustedPaidBreakEarnings,
    extraBreakHours,
  };
};

const StaffWorkReport = () => {
  const { UserName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [startDate, setStartDate] = useState(moment().subtract(6, 'days').toDate());
  const [endDate, setEndDate] = useState(moment().toDate());
  const [filters, setFilters] = useState({
    entryType: 'All',
    searchNotes: '',
    paidStatus: 'All'
  });

  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [compCurrency, setCompCurrency] = useState("GBP");
  
  // Payout states
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [rawUnpaidHours, setRawUnpaidHours] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState(new Set());
  const [payoutReportData, setPayoutReportData] = useState(null);
  const [payoutExpandedDays, setPayoutExpandedDays] = useState(new Set());
  const [payoutForm, setPayoutForm] = useState({
    startDate: moment().startOf('week').toDate(),
    endDate: moment().endOf('week').toDate(),
    payoutType: 'WEEKLY',
    bonusAmount: 0,
    deductionAmount: 0,
    taxAmount: 0,
    notes: ''
  });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayoutID, setProcessingPayoutID] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    paymentReference: '',
    paymentNotes: ''
  });

  const unpaidHours = useMemo(
    () => computeAdjustedUnpaidHours(rawUnpaidHours, staffProfile),
    [rawUnpaidHours, staffProfile]
  );


  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨", // Handle legacy "Rs" code
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Normalize currency code (handle legacy "Rs" -> "PKR")
  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    // Fetch company currency from profile data
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  const fetchStaffData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: { 
          UserName,
          CompID 
        }
      });
      const data = response.data?.data || response.data;
      if (data) {
        setStaffData(data);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff data');
      console.error('Error fetching staff data:', err);
    }
  }, [UserName, CompID]);



  const fetchWorkReport = useCallback(async () => {
    if (!staffData?.userName) return;

    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_WORK_REPORT, {
        params: {
          userName: staffData.userName,
          compID: CompID,
          startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
          endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
        }
      });
 
      const data = response.data?.data || response.data;
      if (data) {
        setReportData(data);
      }
 
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch work report';
      setError(errorMsg);
      showErrorToast(errorMsg);
      console.error('Error fetching work report:', err);
    } finally {
      setLoading(false);
    }
  }, [staffData?.userName, CompID, startDate, endDate]);




  useEffect(() => {
    if (UserName && CompID) {
      fetchStaffData();
    }
  }, [UserName, CompID, fetchStaffData]);



  useEffect(() => {
    if (staffData?.userName) {
      fetchWorkReport();
    }
  }, [staffData?.userName, startDate, endDate, fetchWorkReport]);

  // Payout Functions
  const fetchPayouts = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_PAYOUTS, {
        params: {
          compID: CompID,
          userName: UserName,
          pageNumber: 1,
          pageSize: 100
        }
      });

      const data = response.data?.data || response.data || [];
      setPayouts(data);
    } catch (err) {
      showErrorToast('Failed to fetch payouts');
      console.error('Error fetching payouts:', err);
    }
  }, [CompID, UserName]);

  const fetchStaffProfile = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID: CompID,
          UserName: UserName
        }
      });

      const data = response.data?.data || response.data;
      if (data) {
        // If it's an array, take the first element, otherwise use the data directly
        setStaffProfile(Array.isArray(data) ? data[0] : data);
      }
    } catch (err) {
      console.error('Error fetching staff profile:', err);
    }
  }, [CompID, UserName]);

  const fetchUnpaidHours = useCallback(async () => {
    if (!CompID || !UserName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_UNPAID_HOURS, {
        params: {
          compID: CompID,
          userName: UserName,
          startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
          endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null
        }
      });

      const data = response.data?.data || response.data || [];
      if (data.length > 0) {
        const unpaidData = data[0];
        const normalized = normalizeUnpaidHours(unpaidData);
        setRawUnpaidHours(normalized);
      } else {
        setRawUnpaidHours(null);
      }
    } catch (err) {
      console.error('Error fetching unpaid hours:', err);
      setRawUnpaidHours(null);
    }
  }, [CompID, UserName, startDate, endDate]);

  const fetchStaffLeaves = useCallback(async () => {
    if (!CompID || !UserName) return;
    
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_LEAVES, {
        params: {
          compId: CompID,
          userName: UserName,
          startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
          endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
        },
      });

      const data = response.data?.data || response.data || [];
      const normalizedLeaves = Array.isArray(data)
        ? data.filter(Boolean)
            .filter(leave => !leave.Payout_ID && !leave.payout_ID) // Exclude leaves already in a payout
            .map((leave) => ({
            SL_ID: leave.sL_ID || leave.SL_ID,
            UserName: leave.UserName || leave.userName || '',
            LeaveType: leave.LeaveType || leave.leaveType || '',
            StartDate: leave.StartDate || leave.startDate,
            EndDate: leave.EndDate || leave.endDate,
            TotalHours: leave.TotalHours || leave.totalHours || 0,
            IsPaid: leave.IsPaid ?? leave.isPaid ?? false,
            Reason: leave.Reason || leave.reason || '',
          }))
        : [];

      setStaffLeaves(normalizedLeaves);
      // For hourly staff, auto-select all paid leaves; for salary staff, start with none selected
      // Wait for staffProfile to be loaded before setting selection
      if (staffProfile) {
        if (staffProfile.isSalaryStaff) {
          setSelectedLeaves(new Set());
        } else {
          // Auto-select paid leaves for hourly staff
          const paidLeaveIds = new Set(
            normalizedLeaves.filter(l => l.IsPaid).map(l => l.SL_ID)
          );
          setSelectedLeaves(paidLeaveIds);
        }
      }
    } catch (error) {
      console.error('Error fetching staff leaves:', error);
      setStaffLeaves([]);
    }
  }, [CompID, UserName, startDate, endDate, staffProfile?.isSalaryStaff]);

  const fetchPayoutPeriodReport = useCallback(async () => {
    if (!staffData?.userName || !CompID) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_WORK_REPORT, {
        params: {
          userName: staffData.userName,
          compID: CompID,
          startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
          endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
        }
      });

      const data = response.data?.data || response.data;
      if (data) {
        setPayoutReportData(data);
      }
    } catch (err) {
      console.error('Error fetching payout period report:', err);
      setPayoutReportData(null);
    }
  }, [staffData?.userName, CompID, startDate, endDate]);

  useEffect(() => {
    if (showPayoutModal) {
      fetchPayouts();
    }
  }, [showPayoutModal, fetchPayouts]);

  useEffect(() => {
    if (showGenerateModal) {
      // Sync payout form dates with main filter dates
      setPayoutForm(prev => ({
        ...prev,
        startDate: startDate,
        endDate: endDate
      }));
      // Ensure staff profile is loaded
      if (!staffProfile) {
        fetchStaffProfile();
      }
      fetchUnpaidHours();
      fetchPayoutPeriodReport();
      // Only fetch leaves if staffProfile is available or will be available
      if (staffProfile) {
        fetchStaffLeaves();
      }
    }
  }, [showGenerateModal, startDate, endDate, staffProfile, fetchUnpaidHours, fetchStaffLeaves, fetchStaffProfile, fetchPayoutPeriodReport]);

  // Fetch leaves when staffProfile becomes available
  useEffect(() => {
    if (showGenerateModal && staffProfile && staffLeaves.length === 0) {
      fetchStaffLeaves();
    }
  }, [showGenerateModal, staffProfile, fetchStaffLeaves]);

  // Fetch report when date range changes (from main filter panel)
  useEffect(() => {
    if (showGenerateModal && staffData?.userName) {
      fetchPayoutPeriodReport();
    }
  }, [showGenerateModal, startDate, endDate, fetchPayoutPeriodReport, staffData?.userName]);

  const handleGeneratePayout = async () => {
    if (!unpaidHours || unpaidHours.payableHours <= 0) {
      showErrorToast('No unpaid hours found for the selected period');
      return;
    }

    // Calculate leave adjustment
    let leaveAdjustment = 0;
    let includedLeaveIds = [];
    
    if (staffProfile?.isSalaryStaff) {
      // For salary staff: handle paid and unpaid leaves differently
      const selectedLeavesList = staffLeaves.filter(l => selectedLeaves.has(l.SL_ID));
      // Calculate daily salary rate
      const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
      const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
      
      selectedLeavesList.forEach(leave => {
        const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
        const proRatedAmount = dailySalary * leaveDays;
        
        if (leave.IsPaid) {
          // PAID leaves: Don't add anything (already included in base salary)
          // leaveAdjustment remains 0 for paid leaves
        } else {
          // UNPAID leaves: Subtract from salary (negative adjustment)
          leaveAdjustment -= proRatedAmount;
        }
        includedLeaveIds.push(leave.SL_ID);
      });
    } else {
      // For hourly staff: include all paid leave hours
      const paidLeaves = staffLeaves.filter(l => l.IsPaid);
      const hourlyRate = staffProfile?.ratePerHour || 0;
      paidLeaves.forEach(leave => {
        leaveAdjustment += (leave.TotalHours || 0) * hourlyRate;
        includedLeaveIds.push(leave.SL_ID);
      });
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.GENERATE_PAYOUT, {
        compID: CompID,
        userName: UserName,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        payoutType: payoutForm.payoutType,
        bonusAmount: parseFloat(payoutForm.bonusAmount) || 0,
        deductionAmount: parseFloat(payoutForm.deductionAmount) || 0,
        taxAmount: parseFloat(payoutForm.taxAmount) || 0,
        notes: payoutForm.notes,
        createdBy: UserName,
        // Add leave information - send as comma-separated string
        includedLeaveIds: includedLeaveIds.length > 0 ? includedLeaveIds.join(',') : null,
        leaveAdjustmentAmount: leaveAdjustment
      });

      if (response.data.status) {
        showSuccessToast('Payout generated successfully!');
        setShowGenerateModal(false);
        fetchPayouts();
        // Reset form
        setPayoutForm({
          startDate: moment().startOf('week').toDate(),
          endDate: moment().endOf('week').toDate(),
          payoutType: 'WEEKLY',
          bonusAmount: 0,
          deductionAmount: 0,
          taxAmount: 0,
          notes: ''
        });
        setStaffLeaves([]);
        setSelectedLeaves(new Set());
      } else {
        showErrorToast(response.data.message || 'Failed to generate payout');
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error generating payout');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayout = async (payoutID) => {
    const confirm = await swal({
      title: "Approve Payout?",
      text: "Are you sure you want to approve this payout?",
      icon: "info",
      buttons: true,
    });

    if (!confirm) return;

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.APPROVE_PAYOUT, {
        payoutID: payoutID,
        compID: CompID,
        approvedBy: UserName
      });

      if (response.data.status) {
        showSuccessToast('Payout approved successfully!');
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || 'Failed to approve payout');
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error approving payout');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayout = (payoutID) => {
    setProcessingPayoutID(payoutID);
    setPaymentForm({
      paymentMethod: '',
      paymentReference: '',
      paymentNotes: ''
    });
    setShowProcessModal(true);
  };

  const confirmProcessPayout = async () => {
    if (!paymentForm.paymentMethod) {
      showErrorToast('Please select a payment method');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.PROCESS_PAYOUT, {
        payoutID: processingPayoutID,
        compID: CompID,
        paymentMethod: paymentForm.paymentMethod,
        paymentReference: paymentForm.paymentReference || '',
        paymentNotes: paymentForm.paymentNotes || '',
        paidBy: UserName
      });

      if (response.data.status) {
        showSuccessToast('Payment processed successfully!');
        setShowProcessModal(false);
        fetchPayouts();
      } else {
        showErrorToast(response.data.message || 'Failed to process payment');
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error processing payment');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-warning';
      case 'APPROVED': return 'bg-info';
      case 'PAID': return 'bg-success';
      case 'CANCELLED': return 'bg-secondary';
      case 'REJECTED': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const toggleDayExpansion = (date) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDays(newExpanded);
  };

  const togglePayoutDayExpansion = (date) => {
    const newExpanded = new Set(payoutExpandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setPayoutExpandedDays(newExpanded);
  };

  // Helper functions for payout period calculations
  const toNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : defaultValue;
  };

  const resolveNormalRate = (entry, dayRecord = {}) => {
    return toNumber(
      entry?.normalHourlyRate ??
      entry?.NormalHourlyRate ??
      entry?.ratePerHour ??
      entry?.RatePerHour ??
      dayRecord?.ratePerHour ??
      staffData?.ratePerHour ??
      staffData?.RatePerHour ??
      payoutReportData?.weekStats?.ratePerHour
    );
  };

  const resolveOvertimeRate = (entry, dayRecord = {}) => {
    return toNumber(
      entry?.overtimeHourlyRate ??
      entry?.OvertimeHourlyRate ??
      dayRecord?.overtimeHourlyRate ??
      staffData?.overTimeHourlyRate ??
      staffData?.OverTimeHourlyRate ??
      payoutReportData?.weekStats?.overTimeHourlyRate ??
      resolveNormalRate(entry, dayRecord)
    );
  };

  const calculatePayoutDayStats = (entries, dayRecord = {}) => {
    const isSalaryStaff = Boolean(staffProfile?.isSalaryStaff);
    
    return entries.reduce((acc, entry) => {
      const workedHours =
        toNumber(entry?.workedHours ?? entry?.Worked_Hours ?? entry?.WorkedHours ?? entry?.worked_hours ?? 0);
      let entryPay = toNumber(entry?.entryPay ?? entry?.EntryPay ?? entry?.entry_pay ?? 0);
      const isBreak = Boolean(entry?.isBreak);
      const isOvertime = Boolean(entry?.isOvertime);
      const breakType = (entry?.breakType || '').toString().toUpperCase();

      const normalRateResolved = resolveNormalRate(entry, dayRecord);
      const overtimeRateResolved = resolveOvertimeRate(entry, dayRecord);

      if (entryPay === 0 && !isSalaryStaff) {
        if (isBreak) {
          if (breakType !== 'UNPAID' && breakType !== 'UN-PAID') {
            entryPay = normalRateResolved * workedHours;
          }
        } else {
          entryPay = (isOvertime ? overtimeRateResolved : normalRateResolved) * workedHours;
        }
      }

      if (isBreak) {
        acc.totalBreakHours += workedHours;
        if (breakType === 'UNPAID' || breakType === 'UN-PAID') {
          acc.unpaidBreakHours += workedHours;
        }
        if (entryPay) {
          acc.totalPay += entryPay;
        }
      } else {
        acc.totalHours += workedHours;
        if (isOvertime) {
          acc.overtimeHours += workedHours;
        } else {
          acc.regularHours += workedHours;
        }
        acc.totalPay += entryPay;
      }

      return acc;
    }, {
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      totalPay: 0
    });
  };

  const payoutPeriodDailyRecords = useMemo(() => {
    if (!payoutReportData?.dailyRecords) return [];

    return payoutReportData.dailyRecords
      .map((dayRecord) => {
        const entries = dayRecord.entries || [];

        if (!entries || entries.length === 0) {
          return null;
        }

        const dayStats = calculatePayoutDayStats(entries, dayRecord);

        const totalBreakHours = dayStats.totalBreakHours || dayRecord.totalBreakHours || 0;
        const unpaidBreakHours = dayStats.unpaidBreakHours || dayRecord.unpaidBreakHours || 0;
        const allowedBreakHours = dayRecord.allowedBreakHours || 0;
        const extraBreakHours = dayRecord.extraBreakHours || 0;
        
        // Use our calculated totalHours (excludes breaks) instead of backend totalHours (includes breaks)
        // dayStats.totalHours = regularHours + overtimeHours (no breaks)
        const calculatedWorkingHours = dayStats.totalHours || 0;
        
        // Calculate paid break hours
        const paidBreakHours = totalBreakHours - unpaidBreakHours;
        
        // Calculate excess paid break hours (paid breaks beyond allowed)
        const excessPaidBreakHours = Math.max(0, paidBreakHours - allowedBreakHours);
        
        // Payable hours = calculatedWorkingHours (regular + overtime, no breaks) 
        // - unpaid break hours (all unpaid breaks)
        // - excess paid break hours (paid breaks beyond allowed)
        // Note: No breaks are added to payable hours, only working hours count
        const payableHours = Math.max(
          calculatedWorkingHours - unpaidBreakHours - excessPaidBreakHours,
          0
        );

        const regularHours = dayStats.regularHours || 0;
        const overtimeHours = dayStats.overtimeHours || 0;
        const workingHours = regularHours + overtimeHours;
        const normalRate =
          entries.find((entry) => !entry.isOvertime && !entry.isBreak && entry.normalHourlyRate > 0)?.normalHourlyRate ||
          dayRecord.ratePerHour ||
          0;
        const overtimeRate =
          entries.find((entry) => entry.isOvertime && entry.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
        const avgHourlyRate = workingHours > 0 ? (dayStats.totalPay || 0) / workingHours : 0;
        const effectiveRate = normalRate || overtimeRate || avgHourlyRate || 0;
        const payableEarnings = !staffProfile?.isSalaryStaff ? payableHours * effectiveRate : 0;

        return {
          ...dayRecord,
          ...dayStats,
          entries,
          entryCount: entries.length,
          computedPayableHours: payableHours,
          computedPayableEarnings: payableEarnings,
          computedEffectiveRate: effectiveRate,
          extraBreakHours,
          allowedBreakHours: dayRecord.allowedBreakHours || 0
        };
      })
      .filter(Boolean);
  }, [payoutReportData?.dailyRecords, staffProfile?.isSalaryStaff]);

  const payoutPeriodWeekStats = useMemo(() => {
    if (!payoutReportData?.weekStats && !payoutPeriodDailyRecords.length) {
      return null;
    }

    if (!payoutPeriodDailyRecords.length) {
      if (payoutReportData?.weekStats) {
        return {
          daysWorked: payoutReportData.weekStats.daysWorked || 0,
          regularHours: payoutReportData.weekStats.regularHours || 0,
          overtimeHours: payoutReportData.weekStats.overtimeHours || 0,
          unpaidBreakHours: payoutReportData.weekStats.unpaidBreakHours || 0,
          totalBreakHours: payoutReportData.weekStats.totalBreakHours || 0,
          extraBreakHours: payoutReportData.weekStats.extraBreakHours || 0,
          totalHours: payoutReportData.weekStats.totalHours || 0,
          totalEarnings: payoutReportData.weekStats.totalEarnings || 0
        };
      }
      return {
        daysWorked: 0,
        regularHours: 0,
        overtimeHours: 0,
        unpaidBreakHours: 0,
        totalBreakHours: 0,
        extraBreakHours: 0,
        totalHours: 0,
        totalEarnings: 0
      };
    }

    const aggregate = payoutPeriodDailyRecords.reduce((acc, dayRecord) => {
      acc.daysWorked += 1;
      acc.regularHours += dayRecord.regularHours || 0;
      acc.overtimeHours += dayRecord.overtimeHours || 0;
      acc.unpaidBreakHours += dayRecord.unpaidBreakHours || 0;
      acc.totalBreakHours += dayRecord.totalBreakHours || 0;
      acc.extraBreakHours += dayRecord.extraBreakHours || 0;
      acc.totalHours += dayRecord.totalHours || 0;
      acc.totalEarnings += dayRecord.computedPayableEarnings || dayRecord.totalPay || 0;
      return acc;
    }, {
      daysWorked: 0,
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      extraBreakHours: 0,
      totalHours: 0,
      totalEarnings: 0
    });

    if (payoutReportData?.weekStats) {
      return {
        ...payoutReportData.weekStats,
        ...aggregate,
        daysWorked: aggregate.daysWorked,
        totalEarnings: staffProfile?.isSalaryStaff
          ? (payoutReportData.weekStats.totalEarnings ?? aggregate.totalEarnings)
          : aggregate.totalEarnings
      };
    }

    return aggregate;
  }, [payoutReportData?.weekStats, payoutPeriodDailyRecords, staffProfile?.isSalaryStaff]);




  const getEntryIcon = (entry) => {
    if (entry.isBreak) {
      return 'fa fa-coffee';
    } else if (entry.isOvertime) {
      return 'fa fa-arrow-trend-up';
    } else {
      return 'fa fa-clock';
    }
  };



  const getEntryBadgeClass = (entry) => {
    if (entry.isBreak) {
      return entry.breakType === 'UNPAID' ? 'bg-warning' : 'bg-success';
    } else if (entry.isOvertime) {
      return 'bg-purple';
    } else {
      return 'bg-primary';
    }
  };




  const getEntryTypeLabel = (entry) => {
    if (entry.isBreak) {
      return `${entry.breakType} Break`;
    } else if (entry.isOvertime) {
      return 'Overtime';
    } else {
      return 'Regular Shift';
    }
  };




  const getEntryPaidStatus = (entry) => {
    if (!entry) return false;

    const interpretBooleanLike = (value) => {
      if (value === undefined || value === null) return null;

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        if (Number.isNaN(value)) return null;
        return value > 0;
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return null;

        if (['true', '1', 'yes', 'y', 'paid', 'complete', 'completed', 'processed'].includes(normalized)) {
          return true;
        }

        if (['false', '0', 'no', 'n', 'unpaid', 'pending', 'notpaid', 'not-paid', 'open', 'outstanding'].includes(normalized)) {
          return false;
        }

        const numericValue = Number(normalized);
        if (!Number.isNaN(numericValue)) {
          return numericValue > 0;
        }

        return null;
      }

      return null;
    };

    const considerValues = (values = []) => {
      let hasFalse = false;
      for (const value of values) {
        const interpreted = interpretBooleanLike(value);
        if (interpreted === true) {
          return { truthy: true, hasFalse };
        }
        if (interpreted === false) {
          hasFalse = true;
        }
      }
      return { truthy: false, hasFalse };
    };

    const paidFlags = considerValues([
      entry.isPaid,
      entry.is_paid,
      entry.isPaidEntry,
      entry.is_Paid,
      entry.IsPaid,
      entry.Is_Paid
    ]);

    if (paidFlags.truthy) return true;

    const statusFlags = considerValues([
      entry.paidStatus,
      entry.paymentStatus,
      entry.paid_status,
      entry.payment_status
    ]);

    if (statusFlags.truthy) return true;

    const payoutRefs = [
      entry.payoutId,
      entry.payoutID,
      entry.payout_id,
      entry.payout_ID,
      entry.PayoutId,
      entry.PayoutID,
      entry.Payout_ID,
      entry.payoutUUID,
      entry.payoutUuid,
      entry.PayoutUUID,
      entry.Payout_Uuid
    ];

    const payoutFlags = considerValues(payoutRefs);
    if (payoutFlags.truthy) return true;

    const paidDateValues = [
      entry.paidDateTime,
      entry.paid_date_time,
      entry.paidDate,
      entry.PaidDate,
      entry.PaidDateTime,
      entry.Paid_DateTime
    ];

    const hasPaidDate = paidDateValues.some((value) => value !== undefined && value !== null && `${value}`.trim() !== '');
    if (hasPaidDate) return true;

    if (paidFlags.hasFalse || statusFlags.hasFalse || payoutFlags.hasFalse) {
      return false;
    }

    return false;
  };




  const formatCurrency = (amount) => {
    try {
      // Use dynamic currency from company settings
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      // Fallback to simple formatting with symbol if currency code is invalid
      console.error('Currency formatting error:', error);
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };




  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };




  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else {
      showErrorToast('Invalid date selected');
    }
  };




  const resetFilters = () => {
    setStartDate(moment().subtract(6, 'days').toDate());
    setEndDate(moment().toDate());
    setFilters({
      entryType: 'All',
      searchNotes: '',
      paidStatus: 'All'
    });
  };




  const applyEntryTypeFilter = (entries) => {
    if (filters.entryType === 'All') return entries;
    
    return entries.filter(entry => {
      if (filters.entryType === 'Regular' && !entry.isBreak && !entry.isOvertime) return true;
      if (filters.entryType === 'Break' && entry.isBreak) return true;
      if (filters.entryType === 'Overtime' && entry.isOvertime) return true;
      return false;
    });
  };




  const applyNotesFilter = (entries) => {
    if (!filters.searchNotes.trim()) return entries;
    
    const searchLower = filters.searchNotes.toLowerCase();
    return entries.filter(entry => 
      entry.notes?.toLowerCase().includes(searchLower)
    );
  };




  const applyPaidStatusFilter = (entries) => {
    if (filters.paidStatus === 'All') return entries;

    return entries.filter((entry) => {
      const isPaid = getEntryPaidStatus(entry);
      return filters.paidStatus === 'Paid' ? isPaid : !isPaid;
    });
  };




  const calculateDayStats = (entries) => {
    return entries.reduce((acc, entry) => {
      const workedHours = entry?.workedHours || 0;
      const entryPay = entry?.entryPay || 0;
      const isBreak = Boolean(entry?.isBreak);
      const isOvertime = Boolean(entry?.isOvertime);
      const breakType = (entry?.breakType || '').toString().toUpperCase();

      if (isBreak) {
        // Breaks should NOT be included in totalHours
        // Only track break hours separately
        acc.totalBreakHours += workedHours;
        if (breakType === 'UNPAID' || breakType === 'UN-PAID') {
          acc.unpaidBreakHours += workedHours;
        }
        if (entryPay) {
          acc.totalPay += entryPay;
        }
      } else {
        // Only non-break entries contribute to totalHours
        acc.totalHours += workedHours;
        if (isOvertime) {
          acc.overtimeHours += workedHours;
        } else {
          acc.regularHours += workedHours;
        }
        acc.totalPay += entryPay;
      }

      return acc;
    }, {
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      totalPay: 0
    });
  };
  const isFullTimeStaff = useMemo(() => {
    if (!staffData) return false;
    const flag = pickValueIgnoreCase(staffData, [
      'isFullTimeStaff',
      'ISFullTimeStaff',
      'IsFullTimeStaff',
      'isFullTime',
      'fullTime',
      'full_time',
      'IsFullTime'
    ]);
    if (typeof flag === 'string') {
      const normalized = flag.toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    if (typeof flag === 'number') {
      return flag === 1;
    }
    return Boolean(flag);
  }, [staffData]);

  const staffCompensationRate = useMemo(() => {
    if (!staffData) return null;
    const raw = pickValueIgnoreCase(staffData, [
      'ratePerHour',
      'RatePerHour',
      'rate_per_hour',
      'salary',
      'Salary',
      'monthlySalary',
      'MonthlySalary'
    ]);
    if (raw === null || raw === undefined) return null;
    const numeric = Number(raw);
    return Number.isNaN(numeric) ? null : numeric;
  }, [staffData]);

  const filteredDailyRecords = useMemo(() => {
    if (!reportData?.dailyRecords) return [];

    return reportData.dailyRecords
      .map((dayRecord) => {
        const entries = applyNotesFilter(
          applyEntryTypeFilter(
            applyPaidStatusFilter(dayRecord.entries || [])
          )
        );

        if (!entries || entries.length === 0) {
          return null;
        }

        const dayStats = calculateDayStats(entries);

        const totalBreakHours = dayStats.totalBreakHours || dayRecord.totalBreakHours || 0;
        const unpaidBreakHours = dayStats.unpaidBreakHours || dayRecord.unpaidBreakHours || 0;
        const allowedBreakHours = dayRecord.allowedBreakHours || 0;
        
        // Use our calculated totalHours (excludes breaks) instead of backend totalHours (includes breaks)
        // dayStats.totalHours = regularHours + overtimeHours (no breaks)
        const workingHours = dayStats.totalHours || 0;
        
        // Calculate paid break hours
        const paidBreakHours = totalBreakHours - unpaidBreakHours;
        
        // Calculate excess paid break hours (paid breaks beyond allowed)
        const excessPaidBreakHours = Math.max(0, paidBreakHours - allowedBreakHours);
        
        // Payable hours = workingHours (regular + overtime, no breaks) 
        // - unpaid break hours (all unpaid breaks)
        // - excess paid break hours (paid breaks beyond allowed)
        // Note: No breaks are added to payable hours, only working hours count
        const payableHours = Math.max(
          workingHours - unpaidBreakHours - excessPaidBreakHours,
          0
        );

        return {
          ...dayRecord,
          ...dayStats,
          entries,
          entryCount: entries.length,
          computedPayableHours: payableHours
        };
      })
      .filter(Boolean);
  }, [reportData?.dailyRecords, filters.entryType, filters.paidStatus, filters.searchNotes]);




  const filteredWeekStats = useMemo(() => {
    if (!reportData?.weekStats && !filteredDailyRecords.length) {
      return null;
    }

    if (!filteredDailyRecords.length) {
      if (reportData?.weekStats) {
        return {
          daysWorked: reportData.weekStats.daysWorked || 0,
          regularHours: reportData.weekStats.regularHours || 0,
          overtimeHours: reportData.weekStats.overtimeHours || 0,
          unpaidBreakHours: reportData.weekStats.unpaidBreakHours || 0,
          totalBreakHours: reportData.weekStats.totalBreakHours || 0,
          totalHours: reportData.weekStats.totalHours || 0,
          totalEarnings: reportData.weekStats.totalEarnings || 0
        };
      }

      return {
        daysWorked: 0,
        regularHours: 0,
        overtimeHours: 0,
        unpaidBreakHours: 0,
        totalBreakHours: 0,
        totalHours: 0,
        totalEarnings: 0
      };
    }

    const aggregate = filteredDailyRecords.reduce((acc, dayRecord) => {
      acc.daysWorked += 1;
      acc.regularHours += dayRecord.regularHours || 0;
      acc.overtimeHours += dayRecord.overtimeHours || 0;
      acc.unpaidBreakHours += dayRecord.unpaidBreakHours || 0;
      acc.totalBreakHours += dayRecord.totalBreakHours || 0;
      acc.totalHours += dayRecord.totalHours || 0;
      acc.totalEarnings += dayRecord.totalPay || 0;
      return acc;
    }, {
      daysWorked: 0,
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      totalEarnings: 0
    });

    if (reportData?.weekStats) {
      return {
        ...reportData.weekStats,
        ...aggregate,
        daysWorked: aggregate.daysWorked
      };
    }

    return aggregate;
  }, [reportData?.weekStats, filteredDailyRecords]);




  const weekStats = filteredWeekStats;




  if (!staffData) {
    return <Preloader />;
  }

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/team/manageTeam">Staff</a></li>
        <li className="breadcrumb-item active">Work Report</li>
      </ol>
      <h1 className="page-header">
        Staff Work Report <small>{staffData.FirstName} {staffData.LastName}</small>
      </h1>

      <div className="card border-0">
        {/* Filters */}
        <div className="mb-4">
          <div className="card filter-card" style={{ position: "relative", zIndex: 1 }}>
            <div className="card-body p-4">
              {/* Filter Header */}
              <div className="filter-header" style={{ 
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)` 
              }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-filter me-2"></i>
                    <h6 className="mb-0 fw-semibold">Filter Work Report</h6>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="clear-btn"
                      onClick={resetFilters}
                      title="Clear all filters"
                    >
                      <i className="fa fa-times me-1"></i>
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              {/* Filter Controls */}
              <div className="row g-3">
                {/* Search Notes */}
                <div className="col-xl-4 col-lg-4 col-md-6">
                  <label className="filter-label">
                    <i className="fa fa-search"></i>
                    Search Notes
                  </label>
                  <div className="search-input filter-control">
                    <input
                      type="text"
                      className="filter-input"
                      value={filters.searchNotes}
                      onChange={(e) => handleFilterChange('searchNotes', e.target.value)}
                      placeholder="Search in notes..."
                    />
                    <button className="search-icon" type="button">
                      <i className="fa fa-search"></i>
                    </button>
                  </div>
                </div>

                {/* Entry Type Dropdown */}
                <div className="col-xl-3 col-lg-4 col-md-6">
                  <label className="filter-label">
                    <i className="fa fa-list-check"></i>
                    Entry Type
                  </label>
                  <div className="filter-control">
                    <select
                      className="form-select filter-input"
                      value={filters.entryType}
                      onChange={(e) => handleFilterChange('entryType', e.target.value)}
                    >
                      <option value="All">All Entries</option>
                      <option value="Regular">Regular Shifts</option>
                      <option value="Break">Breaks</option>
                      <option value="Overtime">Overtime</option>
                    </select>
                  </div>
                </div>

                {/* Date Range Picker */}
                <div className="col-xl-3 col-lg-4 col-md-6">
                  <label className="filter-label">
                    <i className="fa fa-calendar"></i>
                    Date Range
                  </label>
                  <div className="filter-control">
                    <div style={{ position: 'relative', zIndex: 1000 }}>
                      <DateRangePicker
                        startDate={startDate && moment(startDate).isValid() ? startDate : null}
                        endDate={endDate && moment(endDate).isValid() ? endDate : null}
                        onChange={handleDateRangeChange}
                        placeholder="Select Date Range"
                        className="filter-dropdown"
                      />
                    </div>
                  </div>
                </div>

                {/* Paid Status Dropdown */}
                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="filter-label">
                    <i className="fa fa-money-bill"></i>
                    Paid Status
                  </label>
                  <div className="filter-control">
                    <select
                      className="form-select filter-input"
                      value={filters.paidStatus}
                      onChange={(e) => handleFilterChange('paidStatus', e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Paid">Paid</option>
                      <option value="Unpaid">Unpaid</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Bottom Controls */}
              <div className="bottom-controls">
                <div className="d-flex align-items-center justify-content-between">
                  {/* Left side - Stats */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center">
                      <span className="control-label mb-0">
                        <i className="fa fa-calendar-days"></i>
                        Showing:
                      </span>
                      <span className="stats-text">
                        {filteredDailyRecords.length} days
                      </span>
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        const startDateStr = startDate ? moment(startDate).format('YYYY-MM-DD') : '';
                        const endDateStr = endDate ? moment(endDate).format('YYYY-MM-DD') : '';
                        const queryParams = startDateStr && endDateStr ? `?startDate=${startDateStr}&endDate=${endDateStr}` : '';
                        navigate(`/team/workReport/${UserName}${queryParams}`);
                      }}
                      style={{
                        backgroundColor: '#007bff',
                        borderColor: '#007bff',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="fa fa-list-alt me-1"></i>
                      View Detailed Report
                    </button>
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => setShowPayoutModal(true)}
                      style={{
                        backgroundColor: '#17a2b8',
                        borderColor: '#17a2b8',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="fa fa-wallet me-1"></i>
                      View Payouts
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => setShowGenerateModal(true)}
                      style={{
                        backgroundColor: '#28a745',
                        borderColor: '#28a745',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="fa fa-money-bill-wave me-1"></i>
                      Generate Payout
                    </button>
                    <button
                      className="refresh-btn"
                      onClick={fetchWorkReport}
                      disabled={loading}
                    >
                      <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && <Preloader />}

        {/* Week Summary Cards */}
        {weekStats && (
          <div className="row mb-4">
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-gray-800 text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-clock fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{weekStats.totalHours.toFixed(2)}</div>
                      <div className="text-white text-opacity-50 fw-bold">Total Hours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-success text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-user-check fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{weekStats.regularHours.toFixed(2)}</div>
                      <div className="text-white text-opacity-50 fw-bold">Regular Hours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-purple text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-arrow-trend-up fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{weekStats.overtimeHours.toFixed(2)}</div>
                      <div className="text-white text-opacity-50 fw-bold">Overtime Hours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-warning text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-coffee fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{weekStats.totalBreakHours.toFixed(2)}</div>
                      <div className="text-white text-opacity-50 fw-bold">Break Hours</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-primary text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-money-bill-wave fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{formatCurrency(weekStats.totalEarnings)}</div>
                      <div className="text-white text-opacity-50 fw-bold">Total Earnings</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="card border-0 mb-3 bg-info text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-calendar-days fa-2x opacity-5"></i>
                    <div className="flex-grow-1 ms-3">
                      <div className="fs-5 fw-bold">{weekStats.daysWorked}</div>
                      <div className="text-white text-opacity-50 fw-bold">Days Worked</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Records */}
        {filteredDailyRecords && filteredDailyRecords.length > 0 ? (
          <Panel>
            <PanelHeader>
              <i className="fa fa-calendar-days me-2"></i>
              Daily Time Records
              <span className="badge bg-primary ms-2">{filteredDailyRecords.length} days</span>
            </PanelHeader>
            <PanelBody className="p-0">
              {filteredDailyRecords.map((dayRecord, index) => {
                const isExpanded = expandedDays.has(dayRecord.date);
                // Use computedPayableHours if available, otherwise calculate from working hours
                // Never use backend totalHours as it includes breaks
                const payableHours = dayRecord.computedPayableHours ?? 
                  (dayRecord.regularHours + dayRecord.overtimeHours - 
                   (dayRecord.unpaidBreakHours || 0) - 
                   Math.max(0, ((dayRecord.totalBreakHours || 0) - (dayRecord.unpaidBreakHours || 0)) - (dayRecord.allowedBreakHours || 0))) ?? 0;
                const totalBreakHours = dayRecord.totalBreakHours || dayRecord.unpaidBreakHours || 0;
                
                // Calculate hourly rates from entries (excluding ALL break hours)
                const regularHours = dayRecord.regularHours || 0;
                const overtimeHours = dayRecord.overtimeHours || 0;
                const workingHours = regularHours + overtimeHours; // Excludes all break hours
                const normalRate = dayRecord.entries.find(e => !e.isOvertime && e.normalHourlyRate > 0)?.normalHourlyRate || 0;
                const overtimeRate = dayRecord.entries.find(e => e.isOvertime && e.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
                const avgHourlyRate = workingHours > 0 ? dayRecord.totalPay / workingHours : 0;
                
                return (
                  <div key={dayRecord.date} className={`${index !== 0 ? 'border-top' : ''}`}>
                    {/* Day Summary Row */}
                    <div 
                      className="p-3 cursor-pointer hover-bg-light"
                      onClick={() => toggleDayExpansion(dayRecord.date)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="row align-items-center">
                        <div className="col-auto">
                          <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-muted`}></i>
                        </div>
                        <div className="col">
                          <div className="d-flex align-items-center">
                            <h6 className="mb-0 me-3">
                              {formatDate(dayRecord.date)}
                            </h6>
                            <span className="badge bg-secondary me-2">
                              {dayRecord.entries.length} entries
                            </span>
                            {dayRecord.totalHours > 24 && (
                              <span className="badge bg-dark me-2">
                                <i className="fa fa-moon me-1"></i>
                                Overnight
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-auto">
                          <div className="row g-2 text-end">
                            {/* Regular Hours */}
                            <div className="col-auto">
                              <div className="text-muted small">Regular</div>
                              <div className="fw-semibold text-success">
                                {dayRecord.regularHours.toFixed(2)} hrs
                              </div>
                            </div>
                            
                            {/* Overtime Hours */}
                            {(dayRecord.overtimeHours > 0) && (
                              <div className="col-auto">
                                <div className="text-muted small">Overtime</div>
                                <div className="fw-semibold text-purple">
                                  {dayRecord.overtimeHours.toFixed(2)} hrs
                                </div>
                              </div>
                            )}
                            
                            {/* Break Hours */}
                            {totalBreakHours > 0 && (
                              <div className="col-auto">
                                <div className="text-muted small">Break</div>
                                <div className="fw-semibold text-warning">
                                  {totalBreakHours.toFixed(2)} hrs
                                </div>
                              </div>
                            )}
                            
                            {/* Total Payable Hours */}
                            <div className="col-auto ps-3 border-start">
                              <div className="text-muted small">Payable</div>
                              <div className="fw-bold text-primary">
                                {payableHours.toFixed(2)} hrs
                              </div>
                            </div>
                            
                            {/* Rate / Salary */}
                            {isFullTimeStaff ? (
                              <div className="col-auto">
                                <div className="text-muted small">Monthly Salary</div>
                                <div className="fw-semibold text-info" style={{ fontSize: '0.85rem' }}>
                                  {staffCompensationRate !== null ? formatCurrency(staffCompensationRate) : 'Salary not set'}
                                </div>
                              </div>
                            ) : (
                              <div className="col-auto">
                                <div className="text-muted small">
                                  {dayRecord.overtimeHours > 0 ? 'Avg Rate' : 'Rate'}
                                </div>
                                <div className="d-flex flex-column">
                                  {(normalRate > 0) && (
                                    <div className="fw-semibold text-info" style={{ fontSize: '0.85rem' }}>
                                      {formatCurrency(normalRate)}/hr
                                    </div>
                                  )}
                                  {(overtimeRate > 0) && (
                                    <div className="fw-semibold text-purple" style={{ fontSize: '0.85rem' }}>
                                      {formatCurrency(overtimeRate)}/hr OT
                                    </div>
                                  )}
                                  {(!normalRate && !overtimeRate && avgHourlyRate > 0) && (
                                    <div className="fw-semibold text-info">
                                      {formatCurrency(avgHourlyRate)}/hr
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Total Earnings */}
                            <div className="col-auto ps-3 border-start">
                              <div className="text-muted small">Earnings</div>
                              <div className="fw-bold text-primary fs-6">
                                {formatCurrency(dayRecord.totalPay)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Entries */}
                    {isExpanded && (
                      <div className="px-3 pb-3">
                        {dayRecord.entries.map((entry) => {
                          const isEntryPaid = getEntryPaidStatus(entry);

                          return (
                            <div key={entry.timeTracking_ID} className="border rounded p-3 mb-2 bg-light">
                            <div className="row align-items-center">
                              <div className="col-auto">
                                <i className={`${getEntryIcon(entry)} fa-lg`}></i>
                              </div>
                              <div className="col">
                                <div className="d-flex align-items-center mb-1">
                                  <h6 className="mb-0 me-2">
                                    {entry.startTime} - {entry.endTime || 'Active'}
                                  </h6>
                                  <span className={`badge ${getEntryBadgeClass(entry)} me-2`}>
                                    {getEntryTypeLabel(entry)}
                                  </span>
                                  <span className={`badge ${isEntryPaid ? 'bg-success' : 'bg-danger'} me-2`}>
                                    {isEntryPaid ? 'Paid' : 'Unpaid'}
                                  </span>
                                  {entry.isLate && <span className="badge bg-danger me-1">Late</span>}
                                  {entry.isEarly && <span className="badge bg-info me-1">Early</span>}
                                  {entry.isManualEntry && <span className="badge bg-secondary me-1">Manual</span>}
                                  {entry.isOvernightShift && <span className="badge bg-dark me-1">Overnight</span>}
                                  {entry.isActiveShift && <span className="badge bg-success me-1">Active</span>}
                                </div>
                                <div className="text-muted small">
                                  {entry.workedHours.toFixed(2)} hours

                                  {/* Rate / Salary Display */}
                                  {isFullTimeStaff ? (
                                    <span className="ms-2 text-info fw-semibold">
                                      <i className="fa fa-wallet me-1"></i>
                                      {staffCompensationRate !== null ? `${formatCurrency(staffCompensationRate)} / month` : 'Salary not set'}
                                    </span>
                                  ) : (
                                    <>
                                      {(entry.isOvertime && entry.overtimeHourlyRate > 0) ? (
                                        <span className="ms-2 text-purple fw-semibold">
                                          <i className="fa fa-clock me-1"></i>
                                          {formatCurrency(entry.overtimeHourlyRate)}/hr (OT)
                                        </span>
                                      ) : (entry.normalHourlyRate > 0) && (
                                        <span className="ms-2 text-info fw-semibold">
                                          <i className="fa fa-clock me-1"></i>
                                          {formatCurrency(entry.normalHourlyRate)}/hr
                                        </span>
                                      )}

                                      {entry.entryPay > 0 && (
                                        <span className="ms-2 fw-bold text-success">
                                          {formatCurrency(entry.entryPay)}
                                        </span>
                                      )}
                                    </>
                                  )}
                                  {entry.deviceName && (
                                    <span className="ms-2">
                                      <i className="fa fa-mobile me-1"></i>
                                      {entry.deviceName}
                                    </span>
                                  )}
                                  {entry.notes && (
                                    <div className="mt-1">
                                      <small className="text-muted">
                                        <i className="fa fa-note-sticky me-1"></i>
                                        "{entry.notes}"
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </PanelBody>
          </Panel>
        ) : !loading && reportData && (
          <Panel>
            <PanelBody className="text-center py-5">
              <i className="fa fa-calendar-xmark fa-3x text-muted mb-3"></i>
              <h5>No Work Records Found</h5>
              <p className="text-muted">
                No work records found for {staffData.FirstName} {staffData.LastName} 
                in the selected date range.
              </p>
              <button className="btn btn-primary" onClick={resetFilters}>
                Reset Filters
              </button>
            </PanelBody>
          </Panel>
        )}
      </div>

      {/* View Payouts Modal */}
      <CustomModal
        isOpen={showPayoutModal}
        onClose={() => {
          setShowPayoutModal(false);
          setSelectedPayout(null);
        }}
        title="Staff Payouts"
        modalSize="modal-xl"
        showButton={false}
        showSubmitButton={false}
        showCancelButton={false}
        maxHeight="80vh"
      >
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Payout History</h6>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setShowPayoutModal(false);
                setShowGenerateModal(true);
              }}
            >
              <i className="fa fa-plus me-1"></i>
              Generate New Payout
            </button>
          </div>

          {payouts.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-money-bill-wave fa-3x text-muted mb-3 d-block"></i>
              <h5>No Payouts Found</h5>
              <p className="text-muted">Generate a payout to get started</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Period</th>
                    <th className="text-center">Type</th>
                    <th className="text-center">Working Hours</th>
                    <th className="text-center">Regular</th>
                    <th className="text-center">Overtime</th>
                    <th className="text-end">Net Amount</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((item) => {
                    const payout = item.payout || item;
                    return (
                      <tr key={payout.payout_ID || payout.Payout_ID}>
                        <td>
                          <div className="fw-semibold">
                            {moment(payout.period_StartDate || payout.Period_StartDate).format('MMM DD')} - {moment(payout.period_EndDate || payout.Period_EndDate).format('MMM DD, YYYY')}
                          </div>
                          <div className="text-muted small">
                            Created: {moment(payout.createdDateTime || payout.CreatedDateTime).format('MMM DD, YYYY')}
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">
                            {payout.payout_Type || payout.Payout_Type}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="fw-semibold">
                            {(payout.total_Working_Hours || payout.Total_Working_Hours || 0).toFixed(1)} hrs
                          </span>
                        </td>
                        <td className="text-center">
                          {(payout.total_Regular_Hours || payout.Total_Regular_Hours || 0).toFixed(1)} hrs
                        </td>
                        <td className="text-center">
                          {(payout.total_Overtime_Hours || payout.Total_Overtime_Hours || 0).toFixed(1)} hrs
                        </td>
                        <td className="text-end">
                          <div className="fw-bold fs-6">
                            {formatCurrency(payout.net_Amount || payout.Net_Amount || 0)}
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${getStatusBadgeClass(payout.payout_Status || payout.Payout_Status)}`}>
                            {payout.payout_Status || payout.Payout_Status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            {(payout.payout_Status || payout.Payout_Status) === 'PENDING' && (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleApprovePayout(payout.payout_ID || payout.Payout_ID)}
                                title="Approve Payout"
                              >
                                <i className="fa fa-check me-1"></i>
                                Approve
                              </button>
                            )}
                            {(payout.payout_Status || payout.Payout_Status) === 'APPROVED' && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleProcessPayout(payout.payout_ID || payout.Payout_ID)}
                                title="Process Payment"
                              >
                                <i className="fa fa-money-bill me-1"></i>
                                Pay
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setSelectedPayout(payout)}
                              title="View Details"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Payout Details */}
          {selectedPayout && (
            <div className="mt-4 border-top pt-4">
              <h6 className="mb-3">Payout Details</h6>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <div className="card border-0 bg-primary text-white">
                    <div className="card-body p-3">
                      <div className="small">Working Hours</div>
                      <div className="fs-4 fw-bold">{(selectedPayout.total_Working_Hours || selectedPayout.Total_Working_Hours || 0).toFixed(1)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-success text-white">
                    <div className="card-body p-3">
                      <div className="small">Regular Hours</div>
                      <div className="fs-4 fw-bold">{(selectedPayout.total_Regular_Hours || selectedPayout.Total_Regular_Hours || 0).toFixed(1)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-purple text-white">
                    <div className="card-body p-3">
                      <div className="small">Overtime Hours</div>
                      <div className="fs-4 fw-bold">{(selectedPayout.total_Overtime_Hours || selectedPayout.Total_Overtime_Hours || 0).toFixed(1)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-info text-white">
                    <div className="card-body p-3">
                      <div className="small">Net Amount</div>
                      <div className="fs-4 fw-bold">{formatCurrency(selectedPayout.net_Amount || selectedPayout.Net_Amount || 0)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td>Regular Earnings:</td>
                        <td className="text-end">{formatCurrency(selectedPayout.regular_Earnings || selectedPayout.Regular_Earnings || 0)}</td>
                      </tr>
                      <tr>
                        <td>Overtime Earnings:</td>
                        <td className="text-end">{formatCurrency(selectedPayout.overtime_Earnings || selectedPayout.Overtime_Earnings || 0)}</td>
                      </tr>
                      <tr>
                        <td>Bonus:</td>
                        <td className="text-end text-success">+{formatCurrency(selectedPayout.bonus_Amount || selectedPayout.Bonus_Amount || 0)}</td>
                      </tr>
                      <tr>
                        <td>Deductions:</td>
                        <td className="text-end text-danger">-{formatCurrency(selectedPayout.deduction_Amount || selectedPayout.Deduction_Amount || 0)}</td>
                      </tr>
                      <tr className="fw-bold">
                        <td>Gross Amount:</td>
                        <td className="text-end">{formatCurrency(selectedPayout.gross_Amount || selectedPayout.Gross_Amount || 0)}</td>
                      </tr>
                      <tr>
                        <td>Tax:</td>
                        <td className="text-end text-danger">-{formatCurrency(selectedPayout.tax_Amount || selectedPayout.Tax_Amount || 0)}</td>
                      </tr>
                      <tr className="fw-bold border-top">
                        <td className="fs-5">Net Amount:</td>
                        <td className="text-end text-primary fs-5">{formatCurrency(selectedPayout.net_Amount || selectedPayout.Net_Amount || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomModal>

      {/* Generate Payout Modal */}
      <CustomModal
        isOpen={showGenerateModal}
        onClose={() => {
          setShowGenerateModal(false);
          setPayoutExpandedDays(new Set());
          setPayoutReportData(null);
        }}
        title="Generate Payout"
        modalSize="modal-xl"
        showButton={true}
        submitButtonText="Generate Payout"
        onSubmit={handleGeneratePayout}
        submitButtonDisabled={!unpaidHours || unpaidHours.payableHours <= 0}
        maxHeight="90vh"
      >
        <div>
          {/* Unpaid Hours Summary */}
          {unpaidHours && (
            <div className="alert alert-info mb-4">
              <h6 className="mb-3">Unpaid Hours Summary</h6>
              <div className="row g-3">
                <div className="col-md-3">
                  <div><strong>Payable Hours:</strong></div>
                  <div className="fs-5 fw-bold text-primary">{unpaidHours.payableHours.toFixed(1)} hrs</div>
                  <div className="text-muted small mt-1">
                    Recorded: {unpaidHours.totalWorkingHours.toFixed(1)} hrs
                  </div>
                  {unpaidHours.unpaidBreakAdjustmentHours > 0 && (
                    <div className="text-muted small text-danger">
                      -{unpaidHours.unpaidBreakAdjustmentHours.toFixed(1)} hrs unpaid breaks
                    </div>
                  )}
                  {(unpaidHours.extraBreakHours || 0) > 0 && (
                    <div className="text-muted small text-danger">
                      -{(unpaidHours.extraBreakHours || 0).toFixed(1)} hrs extra breaks
                    </div>
                  )}
                  <div className="text-muted small mt-1">
                    Regular: {unpaidHours.payableRegularHours.toFixed(1)} hrs
                    {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                      <span className="text-success"> + {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} paid breaks</span>
                    )}
                  </div>
                  <div className="text-muted small">
                    Overtime: {unpaidHours.payableOvertimeHours.toFixed(1)} hrs
                  </div>
                </div>
                <div className="col-md-3">
                  <div><strong>Break Hours:</strong></div>
                  <div className="fs-5 fw-bold text-warning">
                    {(unpaidHours.totalBreakHours || 0).toFixed(1)} hrs
                  </div>
                  {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                    <div className="text-success small mt-1">
                      Paid: {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} hrs
                      {((unpaidHours.extraBreakHours || 0) > 0) && (
                        <span className="text-muted"> ({(unpaidHours.paidBreakHours || 0).toFixed(1)} - {(unpaidHours.extraBreakHours || 0).toFixed(1)} extra)</span>
                      )}
                    </div>
                  )}
                  {(unpaidHours.unpaidBreakHours || 0) > 0 && (
                    <div className="text-muted small">
                      Unpaid: {(unpaidHours.unpaidBreakHours || 0).toFixed(1)} hrs
                    </div>
                  )}
                  {(unpaidHours.extraBreakHours || 0) > 0 && (
                    <div className="text-danger small">
                      Extra: {(unpaidHours.extraBreakHours || 0).toFixed(1)} hrs (deducted)
                    </div>
                  )}
                </div>
                <div className="col-md-3">
                  <div><strong>Earnings Breakdown:</strong></div>
                  <div className="small">
                    <div>Regular: {formatCurrency(unpaidHours.payableRegularEarnings || 0)}</div>
                    <div>Overtime: {formatCurrency(unpaidHours.payableOvertimeEarnings || 0)}</div>
                    {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                      <div className="text-muted small">
                        (includes {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} hrs paid breaks)
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div><strong>Total Earnings:</strong></div>
                  <div className="fs-5 fw-bold text-success">
                    {formatCurrency(unpaidHours.payableTotalEarnings || 0)}
                  </div>
                  {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                    <div className="text-success small mt-1">
                      Includes paid breaks
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Leave Information */}
          {staffLeaves.length > 0 && (
            staffProfile ? (
              <div className={`alert mb-4 ${staffProfile.isSalaryStaff ? 'alert-warning' : 'alert-success'}`}>
                <h6 className="mb-3">
                  <i className="fa fa-calendar-times me-2"></i>
                  Leave Records ({staffLeaves.length})
                </h6>
                {staffProfile.isSalaryStaff ? (
                  <div>
                    <p className="mb-3">
                      <i className="fa fa-info-circle me-1"></i>
                      Select which leaves to process in the salary calculation. Paid leaves are already included in base salary. Unpaid leaves will be deducted from the total.
                    </p>
                    <div className="row g-2">
                      {staffLeaves.map((leave) => {
                        const isSelected = selectedLeaves.has(leave.SL_ID);
                        const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                        const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
                        const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                        const proRatedAmount = dailySalary * leaveDays;
                        
                        return (
                          <div key={leave.SL_ID} className="col-12">
                            <div className="card border">
                              <div className="card-body p-3">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const newSelected = new Set(selectedLeaves);
                                      if (e.target.checked) {
                                        newSelected.add(leave.SL_ID);
                                      } else {
                                        newSelected.delete(leave.SL_ID);
                                      }
                                      setSelectedLeaves(newSelected);
                                    }}
                                    id={`leave-${leave.SL_ID}`}
                                  />
                                  <label className="form-check-label w-100" htmlFor={`leave-${leave.SL_ID}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <strong>{leave.LeaveType || 'Leave'}</strong>
                                        <span className={`badge ms-2 ${leave.IsPaid ? 'bg-success' : 'bg-secondary'}`}>
                                          {leave.IsPaid ? 'Paid' : 'Unpaid'}
                                        </span>
                                        <div className="text-muted small mt-1">
                                          {moment(leave.StartDate).format('MMM DD')} - {moment(leave.EndDate).format('MMM DD, YYYY')} ({leaveDays} day{leaveDays !== 1 ? 's' : ''})
                                        </div>
                                        {leave.Reason && (
                                          <div className="text-muted small">
                                            <i className="fa fa-note-sticky me-1"></i>
                                            {leave.Reason}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <div className={`fw-bold ${isSelected ? (leave.IsPaid ? 'text-info' : 'text-danger') : 'text-muted'}`}>
                                          {isSelected ? (
                                            leave.IsPaid ? `${formatCurrency(0)} (Included)` : `-${formatCurrency(proRatedAmount)}`
                                          ) : (
                                            formatCurrency(proRatedAmount)
                                          )}
                                        </div>
                                        <div className="text-muted small">
                                          {isSelected ? (
                                            leave.IsPaid ? 'Already included' : 'Will be deducted'
                                          ) : (
                                            leave.IsPaid ? 'Paid leave' : 'Unpaid leave'
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <strong>Total Leave Adjustment:</strong>{' '}
                      <span className={`fw-bold ${(() => {
                        const selectedLeavesList = staffLeaves.filter(l => selectedLeaves.has(l.SL_ID));
                        const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
                        const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                        const total = selectedLeavesList.reduce((sum, leave) => {
                          const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                          const proRatedAmount = dailySalary * leaveDays;
                          // Paid leaves: 0 (already included), Unpaid leaves: subtract
                          return sum + (leave.IsPaid ? 0 : -proRatedAmount);
                        }, 0);
                        return total >= 0 ? 'text-success' : 'text-danger';
                      })()}`}>
                        {(() => {
                          const selectedLeavesList = staffLeaves.filter(l => selectedLeaves.has(l.SL_ID));
                          const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
                          const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                          const total = selectedLeavesList.reduce((sum, leave) => {
                            const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                            const proRatedAmount = dailySalary * leaveDays;
                            // Paid leaves: 0 (already included), Unpaid leaves: subtract
                            return sum + (leave.IsPaid ? 0 : -proRatedAmount);
                          }, 0);
                          return formatCurrency(Math.abs(total)) + (total < 0 ? ' (deduction)' : '');
                        })()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="mb-2">
                      <i className="fa fa-info-circle me-1"></i>
                      Paid leave hours will be automatically included in the payout calculation.
                    </p>
                    <div className="row g-2">
                      {staffLeaves.filter(l => l.IsPaid).map((leave) => {
                        const hourlyRate = staffProfile.ratePerHour || 0;
                        const leaveEarnings = (leave.TotalHours || 0) * hourlyRate;
                        
                        return (
                          <div key={leave.SL_ID} className="col-md-6">
                            <div className="card border border-success">
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{leave.LeaveType || 'Leave'}</strong>
                                    <span className="badge bg-success ms-2">Paid</span>
                                    <div className="text-muted small">
                                      {moment(leave.StartDate).format('MMM DD')} - {moment(leave.EndDate).format('MMM DD')}
                                    </div>
                                    <div className="text-muted small">
                                      {(leave.TotalHours || 0).toFixed(1)} hrs
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <div className="text-success fw-bold">
                                      +{formatCurrency(leaveEarnings)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <strong>Total Leave Earnings:</strong>{' '}
                      <span className="text-success fw-bold">
                        {(() => {
                          const paidLeaves = staffLeaves.filter(l => l.IsPaid);
                          const hourlyRate = staffProfile.ratePerHour || 0;
                          const total = paidLeaves.reduce((sum, leave) => {
                            return sum + ((leave.TotalHours || 0) * hourlyRate);
                          }, 0);
                          return formatCurrency(total);
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-info mb-4">
                <p className="text-muted mb-0">
                  <i className="fa fa-spinner fa-spin me-1"></i>
                  Loading staff information...
                </p>
              </div>
            )
          )}

          {/* Payout Period Summary and Day-wise Breakdown */}
          {payoutPeriodWeekStats && (
            <>
              {/* Summary Cards */}
              <div className="mb-4" style={{ position: 'relative', zIndex: 0 }}>
                <h6 className="mb-3">
                  <i className="fa fa-chart-line me-2"></i>
                  Period Summary
                </h6>
                <div className="d-flex gap-3 flex-wrap">
                  {/* Total Hours */}
                  <div 
                    className="flex-grow-1" 
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: themeColor,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white'
                    }}
                  >
                    <i className="fa fa-clock" style={{ fontSize: '24px', opacity: 0.9 }}></i>
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{payoutPeriodWeekStats.totalHours.toFixed(2)}</div>
                      <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Hours</div>
                    </div>
                  </div>

                  {/* Overtime Hours */}
                  <div 
                    className="flex-grow-1" 
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: themeColor,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white'
                    }}
                  >
                    <i className="fa fa-arrow-trend-up" style={{ fontSize: '24px', opacity: 0.9 }}></i>
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{payoutPeriodWeekStats.overtimeHours.toFixed(2)}</div>
                      <div style={{ fontSize: '12px', opacity: 0.85 }}>Overtime Hours</div>
                    </div>
                  </div>

                  {/* Break Hours */}
                  <div 
                    className="flex-grow-1" 
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: themeColor,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white'
                    }}
                  >
                    <i className="fa fa-coffee" style={{ fontSize: '24px', opacity: 0.9 }}></i>
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{payoutPeriodWeekStats.totalBreakHours.toFixed(2)}</div>
                      <div style={{ fontSize: '12px', opacity: 0.85 }}>Break Hours</div>
                    </div>
                  </div>

                  {/* Total Earnings */}
                  <div 
                    className="flex-grow-1" 
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: themeColor,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white'
                    }}
                  >
                    <i className="fa fa-money-bill-wave" style={{ fontSize: '24px', opacity: 0.9 }}></i>
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(payoutPeriodWeekStats.totalEarnings)}</div>
                      <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Earnings</div>
                    </div>
                  </div>

                  {/* Days Worked */}
                  <div 
                    className="flex-grow-1" 
                    style={{ 
                      minWidth: '150px',
                      backgroundColor: themeColor,
                      borderRadius: '8px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white'
                    }}
                  >
                    <i className="fa fa-calendar-days" style={{ fontSize: '24px', opacity: 0.9 }}></i>
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{payoutPeriodWeekStats.daysWorked}</div>
                      <div style={{ fontSize: '12px', opacity: 0.85 }}>Days Worked</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Records */}
              {payoutPeriodDailyRecords && payoutPeriodDailyRecords.length > 0 && (
                <Panel className="mb-4">
                  <PanelHeader>
                    <i className="fa fa-calendar-days me-2"></i>
                    Daily Time Records
                    <span className="badge bg-primary ms-2">{payoutPeriodDailyRecords.length} days</span>
                  </PanelHeader>
                  <PanelBody className="p-0">
                    {payoutPeriodDailyRecords.map((dayRecord, index) => {
                      const isExpanded = payoutExpandedDays.has(dayRecord.date);
                      const totalBreakHours = dayRecord.totalBreakHours || dayRecord.unpaidBreakHours || 0;
                      const extraBreakHours = dayRecord.extraBreakHours || 0;
                      // Use computedPayableHours if available, otherwise calculate from working hours
                      // Never use backend totalHours as it includes breaks
                      const payableHours = dayRecord.computedPayableHours ?? 
                        (dayRecord.regularHours + dayRecord.overtimeHours - 
                         (dayRecord.unpaidBreakHours || 0) - 
                         Math.max(0, ((dayRecord.totalBreakHours || 0) - (dayRecord.unpaidBreakHours || 0)) - (dayRecord.allowedBreakHours || 0))) ?? 0;
                      
                      const regularHours = dayRecord.regularHours || 0;
                      const overtimeHours = dayRecord.overtimeHours || 0;
                      const workingHours = regularHours + overtimeHours;
                      const normalRate = dayRecord.entries.find(e => !e.isOvertime && e.normalHourlyRate > 0)?.normalHourlyRate || 0;
                      const overtimeRate = dayRecord.entries.find(e => e.isOvertime && e.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
                      const avgHourlyRate = dayRecord.computedEffectiveRate ?? (workingHours > 0 ? dayRecord.totalPay / workingHours : 0);
                      
                      return (
                        <div key={dayRecord.date} className={`${index !== 0 ? 'border-top' : ''}`}>
                          {/* Day Summary Row */}
                          <div 
                            className="p-3 cursor-pointer hover-bg-light"
                            onClick={() => togglePayoutDayExpansion(dayRecord.date)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="row align-items-center">
                              <div className="col-auto">
                                <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-muted`}></i>
                              </div>
                              <div className="col">
                                <div className="d-flex align-items-center">
                                  <h6 className="mb-0 me-3">
                                    {formatDate(dayRecord.date)}
                                  </h6>
                                  <span className="badge bg-secondary me-2">
                                    {dayRecord.entries.length} entries
                                  </span>
                                  {dayRecord.totalHours > 24 && (
                                    <span className="badge bg-dark me-2">
                                      <i className="fa fa-moon me-1"></i>
                                      Overnight
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-auto">
                                <div className="row g-2 text-end">
                                  {/* Total Hours (worked hours - includes all breaks) */}
                                  <div className="col-auto">
                                    <div className="text-muted small">Total</div>
                                    <div className="fw-semibold text-success">
                                      {dayRecord.totalHours.toFixed(2)} hrs
                                    </div>
                                  </div>
                                  
                                  {/* Overtime Hours */}
                                  {(dayRecord.overtimeHours > 0) && (
                                    <div className="col-auto">
                                      <div className="text-muted small">Overtime</div>
                                      <div className="fw-semibold text-purple">
                                        {dayRecord.overtimeHours.toFixed(2)} hrs
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Break Hours */}
                                  {totalBreakHours > 0 && (
                                    <div className="col-auto">
                                      <div className="text-muted small">Break</div>
                                      <div className="fw-semibold text-warning">
                                        {totalBreakHours.toFixed(2)} hrs
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Extra Break Hours */}
                                  {extraBreakHours > 0 && (
                                    <div className="col-auto">
                                      <div className="text-muted small">
                                        Extra Break 
                                        <small className="ms-1" style={{ fontSize: '0.7rem' }}>
                                          (Allowed: {(dayRecord.allowedBreakHours || 0).toFixed(1)}h)
                                        </small>
                                      </div>
                                      <div className="fw-bold text-danger">
                                        +{extraBreakHours.toFixed(2)} hrs
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Total Payable Hours */}
                                  <div className="col-auto ps-3 border-start">
                                    <div className="text-muted small">Payable</div>
                                    <div className="fw-bold text-primary">
                                      {payableHours.toFixed(2)} hrs
                                    </div>
                                  </div>
                                  
                                  {/* Hourly Rate */}
                                  <div className="col-auto">
                                    <div className="text-muted small">
                                      {dayRecord.overtimeHours > 0 ? 'Avg Rate' : 'Rate'}
                                    </div>
                                    <div className="d-flex flex-column">
                                      {(normalRate > 0) && (
                                        <div className="fw-semibold text-info" style={{ fontSize: '0.85rem' }}>
                                          {formatCurrency(normalRate)}/hr
                                        </div>
                                      )}
                                      {(overtimeRate > 0) && (
                                        <div className="fw-semibold text-purple" style={{ fontSize: '0.85rem' }}>
                                          {formatCurrency(overtimeRate)}/hr OT
                                        </div>
                                      )}
                                      {(!normalRate && !overtimeRate && avgHourlyRate > 0) && (
                                        <div className="fw-semibold text-info">
                                          {formatCurrency(avgHourlyRate)}/hr
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Total Earnings */}
                                  <div className="col-auto ps-3 border-start">
                                    <div className="text-muted small">Earnings</div>
                                    <div className="fw-bold text-primary fs-6">
                                      {formatCurrency(dayRecord.computedPayableEarnings ?? dayRecord.totalPay)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Entries */}
                          {isExpanded && (
                            <div className="px-3 pb-3">
                              {dayRecord.entries.map((entry) => {
                                const isEntryPaid = getEntryPaidStatus(entry);

                                return (
                                  <div key={entry.timeTracking_ID} className="border rounded p-3 mb-2 bg-light">
                                    <div className="row align-items-center">
                                      <div className="col-auto">
                                        <i className={`${getEntryIcon(entry)} fa-lg`}></i>
                                      </div>
                                      <div className="col">
                                        <div className="d-flex align-items-center mb-1">
                                          <h6 className="mb-0 me-2">
                                            {entry.startTime} - {entry.endTime || 'Active'}
                                          </h6>
                                          <span className={`badge ${getEntryBadgeClass(entry)} me-2`}>
                                            {getEntryTypeLabel(entry)}
                                          </span>
                                          <span className={`badge ${isEntryPaid ? 'bg-success' : 'bg-danger'} me-2`}>
                                            {isEntryPaid ? 'Paid' : 'Unpaid'}
                                          </span>
                                          {entry.isLate && <span className="badge bg-danger me-1">Late</span>}
                                          {entry.isEarly && <span className="badge bg-info me-1">Early</span>}
                                          {entry.isManualEntry && <span className="badge bg-secondary me-1">Manual</span>}
                                          {entry.isOvernightShift && <span className="badge bg-dark me-1">Overnight</span>}
                                          {entry.isActiveShift && <span className="badge bg-success me-1">Active</span>}
                                        </div>
                                        <div className="text-muted small">
                                          {entry.workedHours.toFixed(2)} hours
                                          
                                          {/* Hourly Rate Display */}
                                          {(entry.isOvertime && entry.overtimeHourlyRate > 0) ? (
                                            <span className="ms-2 text-purple fw-semibold">
                                              <i className="fa fa-clock me-1"></i>
                                              {formatCurrency(entry.overtimeHourlyRate)}/hr (OT)
                                            </span>
                                          ) : (entry.normalHourlyRate > 0) && (
                                            <span className="ms-2 text-info fw-semibold">
                                              <i className="fa fa-clock me-1"></i>
                                              {formatCurrency(entry.normalHourlyRate)}/hr
                                            </span>
                                          )}
                                          
                                          {entry.entryPay > 0 && (
                                            <span className="ms-2 fw-bold text-success">
                                              {formatCurrency(entry.entryPay)}
                                            </span>
                                          )}
                                          {entry.deviceName && (
                                            <span className="ms-2">
                                              <i className="fa fa-mobile me-1"></i>
                                              {entry.deviceName}
                                            </span>
                                          )}
                                          {entry.notes && (
                                            <div className="mt-1">
                                              <small className="text-muted">
                                                <i className="fa fa-note-sticky me-1"></i>
                                                "{entry.notes}"
                                              </small>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </PanelBody>
                </Panel>
              )}
            </>
          )}

          {/* Form */}
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Payout Type</label>
              <select
                className="form-select"
                value={payoutForm.payoutType}
                onChange={(e) => setPayoutForm({ ...payoutForm, payoutType: e.target.value })}
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              >
                <option value="WEEKLY">Weekly</option>
                <option value="BI_WEEKLY">Bi-Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Bonus Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.bonusAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, bonusAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Deduction Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.deductionAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, deductionAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Tax Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.taxAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, taxAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                rows="3"
                placeholder="Enter any notes..."
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              ></textarea>
            </div>
          </div>
        </div>
      </CustomModal>

      {/* Process Payment Modal */}
      <CustomModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        title="Process Payment"
        modalSize="modal-md"
        showButton={true}
        submitButtonText="Process Payment"
        onSubmit={confirmProcessPayout}
        submitButtonDisabled={!paymentForm.paymentMethod}
      >
        <div>
          <div className="mb-3">
            <label className="form-label">Payment Method <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            >
              <option value="">-- Select Payment Method --</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CASH">Cash</option>
              <option value="CHEQUE">Cheque</option>
              <option value="PAYPAL">PayPal</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Payment Reference / Transaction ID</label>
            <input
              type="text"
              className="form-control"
              value={paymentForm.paymentReference}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentReference: e.target.value })}
              placeholder="Enter reference number"
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              value={paymentForm.paymentNotes}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentNotes: e.target.value })}
              rows="3"
              placeholder="Enter any notes..."
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            ></textarea>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default StaffWorkReport;